<script lang="ts" setup>
import { ref, watch } from 'vue';
import CloseModalIcon from "./CloseModalIcon.vue";
import {
  Dialog,
  TransitionRoot,
  TransitionChild,
  DialogPanel,
} from '@headlessui/vue';
import { toRefs } from 'vue';
const props = withDefaults(
  defineProps<{
    dialogPannelClasses?: string;
    modalOuterContainerClasses?: string;
    modalButtonClasses?: string;
    defaultStateOfModal?: boolean;
    isActiveCTA?: boolean;
  }>(),
  {
    dialogPannelClasses: '',
    modalOuterContainerClasses: '',
    modalButtonClasses: '',
    defaultStateOfModal: false,
    isActiveCTA: true,
  }
);
const { dialogPannelClasses, defaultStateOfModal, isActiveCTA } = toRefs(props);

const emits = defineEmits(['modalClosed', 'modalButtonClicked']);

const isOpen = ref(defaultStateOfModal.value);
const openModal = () => {
  if (isActiveCTA.value) {
    isOpen.value = true;
  }
  emits('modalButtonClicked');
};
const closeModal = () => {
  isOpen.value = false;
  emits('modalClosed');
};

watch(defaultStateOfModal, (value) => {
  isOpen.value = value;
});
defineExpose({
  closeModal,
});
</script>
<template>
  <div>
    <TransitionRoot appear :show="isOpen" as="template">
      <Dialog as="div" class="relative z-[101]" @close="closeModal">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="modal-overlay"></div>
        </TransitionChild>

        <div class="fixed inset-0 max-h-full overflow-auto base-scrollbar">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              :class="[
                'modal-dialog',
                dialogPannelClasses,
              ]"
            >
              <div
                :class="[
                  'modal-content',
                  modalOuterContainerClasses,
                ]"
              >
                <CloseModalIcon
                  class="modal-close-icon"
                  @click="closeModal()"
                />
                <div class="modal-body">
                  <slot name="modalContent"></slot>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>
    <div
      v-if="!defaultStateOfModal"
      :class="['w-full h-full modal-btn', modalButtonClasses]"
      @click.stop="openModal()"
    >
      <slot name="modalButton"></slot>
    </div>
  </div>
</template>

<style lang="scss">
.modal-overlay{
  background: #111111cc;
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 9999;
}
.modal-inside {
  .modal-content {
    @apply grid isolation-auto place-content-center justify-center;
  }
  .modal-close-icon {
    z-index: 1;
  }
  .modal-close-icon,
  .modal-body {
    grid-column: 1 / -1;
    grid-row: 1 / -1;
  }
}
.modal-dialog.asset-lightbox-modal{
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 32px;
  @include md{
    padding:56px;
  }
  .modal-content{
    position: relative;
    width:auto;
    max-width:1136px;
    background:transparent;
    padding: 0;
    box-shadow: none !important;
  }
  .modal-close-icon{
    margin-left:auto;
    cursor:pointer;
    padding-bottom:8px;
    margin-bottom: 16px;
  }
  .modal-body{
    overflow:hidden;
    height:100%;
    padding:0;
    .pointer-events-none.opacity-50{
      opacity: .5;
      pointer-events: none;
    }
    &>div {
      height:100%;
      &:focus-visible{
        outline: none;
      }
    }
    img{
      height: 100%;
    }
    &>div>div{
      height:calc(100dvh - 305px);
    }
    .carousel__prev{
      left:0;
    }
    .carousel__next{
      right:0;
    }
    .carousel__prev, .carousel__next{
      background:transparent;
      border:none;
      &:focus-visible{
        outline: none;
      }
      &:before{
        content: none;
      }
    }
  }
}
</style>
